import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';

import type { Cue } from '../audio';
import { BackgroundId, CompletionId, Look } from '../catalog';

import { Backdrop, buildBackdrop, GROUND_Y } from './backdrops';
import {
    applyLayout,
    buildDial,
    buildEyes,
    buildHat,
    buildNeck,
    buildMouth,
    buildVine,
    createBodyGeometry,
    DIAL_MINUTES,
    DialBuild,
    FeatureBuild,
    TIMER_EYES,
    TIMER_MOUTH,
    VineBuild,
} from './builders';
import { Debris } from './debris';
import { Particles } from './particles';

export interface TomatoSceneOptions {
    /** Called at the moments an animation wants a sound (and a buzz). */
    onCue?: (cue: Cue, pitch?: number) => void;
    /** Called when the tomato itself is tapped. */
    onPoke?: () => void;
}

const easeOutCubic = (p: number) => 1 - Math.pow(1 - p, 3);
const easeInCubic = (p: number) => p * p * p;
const easeInQuad = (p: number) => p * p;
const easeOutElastic = (p: number) => {
    if (p === 0 || p === 1) return p;
    return (
        Math.pow(2, -9 * p) * Math.sin((p * 10 - 0.75) * ((2 * Math.PI) / 3)) +
        1
    );
};

const rand = (a: number, b: number) => a + Math.random() * (b - a);
const CONFETTI = [
    0xff3b6b, 0xffd93d, 0x3ddc84, 0x3a86ff, 0xb455ff, 0xff8a3d, 0x2ee6d6,
];

const radialTexture = (): THREE.Texture => {
    const c = document.createElement('canvas');
    c.width = c.height = 128;
    const ctx = c.getContext('2d')!;
    const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.4, 'rgba(255,255,255,0.35)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 128, 128);
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
};

interface Tween {
    t: number;
    d: number;
    fn: (p: number) => void;
    resolve: () => void;
}

interface Part {
    object: THREE.Object3D;
    homePos: THREE.Vector3;
    homeQuat: THREE.Quaternion;
    vel: THREE.Vector3;
    ang: THREE.Vector3;
    flying: boolean;
}

/**
 * The 3D tomato: procedural body, vine and accessories, an idle personality (breathing, squash and
 * stretch, eyes that follow the pointer), and the three completion animations. It is imperative and
 * knows nothing about React or the timer; the wrapper drives it.
 */
export class TomatoScene {
    private readonly container: HTMLElement;
    private readonly opts: TomatoSceneOptions;
    private readonly renderer: THREE.WebGLRenderer;
    private readonly scene = new THREE.Scene();
    private readonly camera = new THREE.PerspectiveCamera(38, 1, 0.1, 1200);
    private readonly camBase = new THREE.Vector3(0, 0.2, 9);
    private readonly stage = new THREE.Group();
    private readonly root = new THREE.Group();
    private readonly spin = new THREE.Group();
    private readonly body = new THREE.Group();
    private readonly bodyMesh: THREE.Mesh;
    private readonly bodyMat: THREE.MeshPhysicalMaterial;
    private readonly shadow: THREE.Mesh;
    private readonly glow: THREE.Mesh;
    private readonly flashSprite: THREE.Mesh;
    private readonly ring: THREE.Mesh;
    private readonly rimLight: THREE.PointLight;
    private readonly additive = new Particles(900, THREE.AdditiveBlending);
    private readonly soft = new Particles(900, THREE.NormalBlending);
    private readonly chunks: Debris;
    private readonly seeds: Debris;
    private readonly confetti: Debris;
    private readonly observer: ResizeObserver;
    private readonly raycaster = new THREE.Raycaster();

    private reserve = 0.32;
    private look: Look | null = null;
    private vine: VineBuild | null = null;
    private eyes: FeatureBuild | null = null;
    private mouth: FeatureBuild | null = null;
    private hat: FeatureBuild | null = null;
    private neck: FeatureBuild | null = null;
    private dial: DialBuild | null = null;
    /** Session length, for the timer model's dial. */
    private totalMs = 25 * 60_000;
    private dialFrac = 1;
    private backdrop: Backdrop | null = null;
    /** The backdrop must re-spread itself over the view (it changed, or the view changed shape). */
    private backdropDirty = false;
    /** Smoothed pointer-driven yaw (x) and pitch (y) of the backdrop, in radians. */
    private readonly orbit = new THREE.Vector2();
    private readonly fogBase = new WeakMap<THREE.Fog, [number, number]>();
    private readonly feet = new THREE.Vector3();
    private yawnAmt = 0;
    private partyHue = 0;
    private readonly parts: Part[] = [];
    private readonly hatMount = new THREE.Group();

    private tweens: Tween[] = [];
    private time = 0;
    private lastFrame = 0;
    private disposed = false;
    private animating = false;
    private progress = 0;
    private running = false;
    private sq = 0;
    private sqV = 0;
    private hop = 0;
    private hopV = 0;
    private shake = 0;
    private flash = 0;
    private beat = 0;
    private dustTimer = 0;
    private pointer = new THREE.Vector2();
    /** After the finger or button is released the tomato slowly turns back to face the screen. */
    private pointerReleased = false;
    private readonly pupilPos = new THREE.Vector2();
    private readonly reducedMotion: boolean;
    private readonly onPointerMove: (e: PointerEvent) => void;
    private readonly onPointerDown: (e: PointerEvent) => void;
    private readonly onPointerUp: () => void;

    constructor(container: HTMLElement, opts: TomatoSceneOptions = {}) {
        this.container = container;
        this.opts = opts;
        this.reducedMotion =
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            powerPreference: 'high-performance',
        });
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.05;
        this.renderer.setClearColor(0x000000, 0);
        // Two passes: the backdrop, then the tomato on top of it.
        this.renderer.autoClear = false;
        const canvas = this.renderer.domElement;
        canvas.style.display = 'block';
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.touchAction = 'none';
        container.appendChild(canvas);

        const pmrem = new THREE.PMREMGenerator(this.renderer);
        this.scene.environment = pmrem.fromScene(
            new RoomEnvironment(),
            0.04
        ).texture;
        this.scene.environmentIntensity = 0.55;
        pmrem.dispose();

        const key = new THREE.DirectionalLight(0xffffff, 2.4);
        key.position.set(3, 5, 6);
        this.scene.add(key, new THREE.HemisphereLight(0xffffff, 0x553355, 0.7));
        this.rimLight = new THREE.PointLight(0xff9a7a, 30, 20, 1.6);
        this.rimLight.position.set(-3.5, 2, -3);
        this.scene.add(this.rimLight);

        this.scene.add(this.stage);
        this.stage.add(this.root);
        this.root.add(this.spin);
        this.spin.add(this.body);

        this.bodyMat = new THREE.MeshPhysicalMaterial({
            color: 0xe63a2a,
            vertexColors: true,
            roughness: 0.5,
            clearcoat: 0.3,
            clearcoatRoughness: 0.3,
            sheen: 0,
            sheenColor: new THREE.Color(0xff7a6a),
            emissive: new THREE.Color(0x000000),
        });
        this.bodyMesh = new THREE.Mesh(createBodyGeometry(), this.bodyMat);
        this.body.add(this.bodyMesh);
        this.hatMount.position.set(0.3, 0.76, 0.14);
        this.hatMount.rotation.set(0.14, 0, -0.38);
        this.body.add(this.hatMount);

        const radial = radialTexture();
        this.shadow = new THREE.Mesh(
            new THREE.PlaneGeometry(2.8, 2.8),
            new THREE.MeshBasicMaterial({
                map: radial,
                color: 0x000000,
                transparent: true,
                opacity: 0.28,
                depthWrite: false,
            })
        );
        this.shadow.rotation.x = -Math.PI / 2;
        this.shadow.position.y = -1.0;
        this.stage.add(this.shadow);

        this.glow = new THREE.Mesh(
            new THREE.PlaneGeometry(1, 1),
            new THREE.MeshBasicMaterial({
                map: radial,
                transparent: true,
                opacity: 0.25,
                depthWrite: false,
                blending: THREE.AdditiveBlending,
            })
        );
        this.glow.position.z = -1.6;
        this.glow.scale.setScalar(6.5);
        this.stage.add(this.glow);

        this.flashSprite = new THREE.Mesh(
            new THREE.PlaneGeometry(1, 1),
            new THREE.MeshBasicMaterial({
                map: radial,
                color: 0xfff1c9,
                transparent: true,
                opacity: 0,
                depthWrite: false,
                blending: THREE.AdditiveBlending,
            })
        );
        this.flashSprite.position.z = 1.2;
        this.flashSprite.renderOrder = 9;
        this.stage.add(this.flashSprite);

        this.ring = new THREE.Mesh(
            new THREE.RingGeometry(0.92, 1, 72),
            new THREE.MeshBasicMaterial({
                color: 0xffffff,
                transparent: true,
                opacity: 0,
                depthWrite: false,
                blending: THREE.AdditiveBlending,
                side: THREE.DoubleSide,
            })
        );
        this.ring.position.z = 0.6;
        this.stage.add(this.ring);

        this.scene.add(this.soft.points, this.additive.points);

        this.chunks = new Debris(
            new THREE.IcosahedronGeometry(0.16, 0),
            new THREE.MeshStandardMaterial({
                roughness: 0.35,
                flatShading: true,
            }),
            110
        );
        this.seeds = new Debris(
            new THREE.SphereGeometry(0.05, 8, 6),
            new THREE.MeshStandardMaterial({ roughness: 0.5, color: 0xffffff }),
            48
        );
        const confettiMat = new THREE.MeshBasicMaterial({
            side: THREE.DoubleSide,
        });
        this.confetti = new Debris(
            new THREE.PlaneGeometry(0.1, 0.17),
            confettiMat,
            320
        );
        this.scene.add(this.chunks.mesh, this.seeds.mesh, this.confetti.mesh);

        this.observer = new ResizeObserver(() => this.resize());
        this.observer.observe(container);
        this.resize();

        this.onPointerMove = (e) => {
            const r = container.getBoundingClientRect();
            if (!r.width || !r.height) return;
            this.pointer.set(
                ((e.clientX - r.left) / r.width) * 2 - 1,
                -(((e.clientY - r.top) / r.height) * 2 - 1)
            );
            this.pointerReleased = false;
        };
        this.onPointerUp = () => {
            this.pointerReleased = true;
        };
        this.onPointerDown = (e) => {
            const r = container.getBoundingClientRect();
            const ndc = new THREE.Vector2(
                ((e.clientX - r.left) / r.width) * 2 - 1,
                -(((e.clientY - r.top) / r.height) * 2 - 1)
            );
            this.pointer.copy(ndc);
            this.pointerReleased = false;
            this.raycaster.setFromCamera(ndc, this.camera);
            if (
                !this.animating &&
                this.raycaster.intersectObject(this.bodyMesh, false).length > 0
            ) {
                this.poke();
                this.opts.onPoke?.();
            }
        };
        window.addEventListener('pointermove', this.onPointerMove, {
            passive: true,
        });
        canvas.addEventListener('pointerdown', this.onPointerDown);
        window.addEventListener('pointerup', this.onPointerUp);
        window.addEventListener('pointercancel', this.onPointerUp);

        this.lastFrame = performance.now();
        this.renderer.setAnimationLoop(this.frame);
    }

    // ------------------------------------------------------------------ public API

    setLook(next: Look) {
        const prev = this.look;
        this.look = next;
        this.bodyMat.color.set(next.color);
        this.rimLight.color.set(
            new THREE.Color(next.color).offsetHSL(0.5, 0, 0.1)
        );
        (this.glow.material as THREE.MeshBasicMaterial).color.set(next.color);

        let changed = false;
        const modelChanged = !prev || prev.model !== next.model;
        if (modelChanged) {
            this.swap('dial', next);
            changed = true;
        }
        if (!prev || prev.vine !== next.vine) {
            this.swap('vine', next);
            changed = true;
        }
        if (!prev || modelChanged || prev.eyes !== next.eyes) {
            this.swap('eyes', next);
            changed = true;
        }
        // The timer's mouth rides above a shirt, so a shirt change re-seats it too.
        const shirtLift = prev?.neck !== next.neck && next.model === 'timer';
        if (modelChanged || shirtLift || prev.mouth !== next.mouth) {
            this.swap('mouth', next);
            changed = true;
        }
        if (!prev || prev.hat !== next.hat) {
            this.swap('hat', next);
            changed = true;
        }
        if (!prev || prev.neck !== next.neck) {
            this.swap('neck', next);
            changed = true;
        }
        this.dial?.setColor(next.color);
        // The stem would poke through a hat.
        this.vine?.stem.forEach((o) => (o.visible = next.hat === 'none'));
        if (prev && changed) {
            this.poke();
            this.sparkle(0.9);
        }
        if (prev && prev.color !== next.color)
            this.beat = Math.max(this.beat, 0.35);
    }

    /** `progress` is 0..1 of the running session; `running` turns on the nervous energy. */
    setProgress(progress: number, running: boolean, totalMs?: number) {
        this.progress = progress;
        this.running = running;
        if (totalMs !== undefined) this.totalMs = totalMs;
    }

    /** A squash-and-stretch bounce with a few sparkles. */
    poke() {
        this.sqV += 5.5;
        this.sparkle(0.5);
    }

    /** The phone was batted like a ping-pong racket: jump, harder for a harder hit (0..1). */
    bounce(strength = 0.5) {
        if (this.animating) return;
        const k = THREE.MathUtils.clamp(strength, 0, 1);
        this.hopV = Math.max(this.hopV, 3.5 + k * 5);
        this.sqV += 3 + k * 4;
        this.sparkle(0.4 + k * 0.8);
    }

    /** Called when a timer starts: a little hype. */
    hype() {
        this.sqV += 7;
        this.beat = 1;
        this.sparkle(1.4);
        this.shakeBy(0.15);
    }

    /** A slow stretch for waking up into light mode. */
    async yawn() {
        if (this.animating || this.reducedMotion) return;
        await this.tween(1.8, (p) => {
            this.yawnAmt = Math.sin(Math.PI * Math.min(1, p)) ** 0.7;
        });
        this.yawnAmt = 0;
    }

    /** Dark mode: a spooky little wobble and a glint. */
    spook() {
        this.sqV += 4;
        this.beat = Math.max(this.beat, 0.5);
        this.sparkle(0.7);
    }

    /** Music reaction: `level` 0..1 is the loudness, `beat` marks a kick. */
    music(level: number, beat: boolean) {
        if (this.animating || !beat) return;
        this.sqV += 3.5 + level * 4;
        this.beat = Math.max(this.beat, 0.4 + level * 0.6);
        this.shakeBy(0.05 + level * 0.1);
        if (level > 0.5) this.sparkle(0.6);
        this.partyHue = (this.partyHue + 0.07) % 1;
        this.rimLight.color.setHSL(this.partyHue, 1, 0.65);
    }

    /** Swaps the scene behind the tomato. `default` leaves the canvas transparent over the page's glow. */
    setBackdrop(id: BackgroundId, dark: boolean, farmSign: string) {
        this.backdrop?.dispose();
        this.backdrop = buildBackdrop(id, dark, farmSign);
        this.backdropDirty = true;
        this.shadow.visible = !this.backdrop?.hideShadow;
        this.shadow.position.y = this.backdrop ? GROUND_Y + 0.02 : -1.0;
    }

    /**
     * The backdrop and the tomato share one camera, so the tomato really stands in the scene's 3D space. The
     * camera looks down at the scene from a little above; it is pitched so the tomato's feet stay on the same
     * screen spot. The world is drawn smaller than the tomato, as if seen from further back, by scaling it about
     * the tomato's feet.
     */
    private aimBackdropCamera(dt: number) {
        const backdrop = this.backdrop!;
        const root = backdrop.root;
        const lift = 2.2;
        const pull = 5;
        const s = this.camBase.z / (this.camBase.z + pull);
        root.position.y = this.stage.position.y;
        root.scale.setScalar(1);
        const cam = this.camera;
        cam.updateMatrixWorld();
        this.feet.set(0, this.stage.position.y + GROUND_Y, 0);
        const ndcY = this.feet.clone().project(cam).y;

        cam.position.y =
            this.feet.y + (cam.position.y + lift - this.feet.y) * s;
        const halfFov = THREE.MathUtils.degToRad(cam.fov) / 2;
        const toFeet = Math.atan2(
            this.feet.y - cam.position.y,
            cam.position.z - this.feet.z
        );
        cam.rotation.set(toFeet - Math.atan(ndcY * Math.tan(halfFov)), 0, 0);
        cam.updateMatrixWorld();

        root.scale.setScalar(s);
        root.position.y = this.stage.position.y + GROUND_Y * (1 - s);
        const fog = backdrop.scene.fog;
        if (fog instanceof THREE.Fog) {
            let base = this.fogBase.get(fog);
            if (!base) {
                base = [fog.near, fog.far];
                this.fogBase.set(fog, base);
            }
            fog.near = base[0] * s;
            fog.far = base[1] * s;
        }
        if (this.backdropDirty) {
            this.backdropDirty = false;
            root.rotation.set(0, 0, 0);
            root.updateMatrixWorld(true);
            backdrop.fit?.(cam);
        }
        if (backdrop.orbit && !this.reducedMotion) {
            // The world swings a few degrees about the tomato as the pointer moves: slow and discreet.
            const ease = 1 - Math.exp(-dt * 1.2);
            this.orbit.x += (this.pointer.x * 0.05 - this.orbit.x) * ease;
            this.orbit.y += (this.pointer.y * 0.025 - this.orbit.y) * ease;
            root.rotation.set(-this.orbit.y, -this.orbit.x, 0);
        }
    }

    /** Smaller celebration, e.g. for finishing a break. */
    celebrate(count = 90) {
        this.sqV += 6;
        this.confettiBurst(new THREE.Vector3(0, 0.6, 0.5), count, 7);
        this.opts.onCue?.('chime');
    }

    get isAnimating() {
        return this.animating;
    }

    async playCompletion(kind: CompletionId): Promise<void> {
        if (this.animating) return;
        this.animating = true;
        try {
            if (kind === 'explode') await this.explode();
            else if (kind === 'roll') await this.rollAway();
            else await this.rocket();
        } finally {
            if (!this.disposed) this.settle();
        }
    }

    dispose() {
        this.disposed = true;
        this.backdrop?.dispose();
        this.renderer.setAnimationLoop(null);
        this.observer.disconnect();
        window.removeEventListener('pointermove', this.onPointerMove);
        window.removeEventListener('pointerup', this.onPointerUp);
        window.removeEventListener('pointercancel', this.onPointerUp);
        this.renderer.domElement.removeEventListener(
            'pointerdown',
            this.onPointerDown
        );
        this.scene.traverse((o) => {
            const m = o as THREE.Mesh;
            m.geometry?.dispose?.();
            const mat = m.material as
                THREE.Material | THREE.Material[] | undefined;
            if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
            else mat?.dispose?.();
        });
        this.additive.dispose();
        this.soft.dispose();
        this.scene.environment?.dispose();
        this.renderer.dispose();
        this.renderer.domElement.remove();
    }

    // ------------------------------------------------------------------ construction helpers

    private swap(
        slot: 'vine' | 'eyes' | 'mouth' | 'hat' | 'neck' | 'dial',
        look: Look
    ) {
        const old = {
            vine: this.vine?.group,
            eyes: this.eyes?.group,
            mouth: this.mouth?.group,
            neck: this.neck?.group,
            hat: this.hat?.group,
            dial: this.dial?.group,
        }[slot];
        if (old) {
            old.removeFromParent();
            this.disposeTree(old);
            const i = this.parts.findIndex((p) => p.object === old);
            if (i >= 0) this.parts.splice(i, 1);
        }
        let group: THREE.Group | null = null;
        if (slot === 'vine') {
            this.vine = buildVine(look.vine);
            group = this.vine.group;
            this.body.add(group);
        } else if (slot === 'eyes') {
            this.eyes = look.eyes === 'none' ? null : buildEyes(look.eyes);
            if (this.eyes && look.model === 'timer')
                applyLayout(this.eyes, TIMER_EYES);
            group = this.eyes?.group ?? null;
            if (group) this.body.add(group);
        } else if (slot === 'mouth') {
            this.mouth = look.mouth === 'none' ? null : buildMouth(look.mouth);
            if (this.mouth && look.model === 'timer')
                applyLayout(this.mouth, {
                    ...TIMER_MOUTH,
                    lift: look.neck === 'tshirt' ? 0.07 : 0,
                });
            group = this.mouth?.group ?? null;
            if (group) this.body.add(group);
        } else if (slot === 'dial') {
            this.dial =
                look.model === 'timer'
                    ? buildDial(this.renderer.capabilities.getMaxAnisotropy())
                    : null;
            group = this.dial?.group ?? null;
            if (group) this.body.add(group);
        } else if (slot === 'neck') {
            this.neck = look.neck === 'none' ? null : buildNeck(look.neck);
            group = this.neck?.group ?? null;
            if (group) this.body.add(group);
        } else {
            this.hat = look.hat === 'none' ? null : buildHat(look.hat);
            group = this.hat?.group ?? null;
            if (group) this.hatMount.add(group);
        }
        if (group) {
            this.parts.push({
                object: group,
                homePos: group.position.clone(),
                homeQuat: group.quaternion.clone(),
                vel: new THREE.Vector3(),
                ang: new THREE.Vector3(),
                flying: false,
            });
        }
    }

    private disposeTree(o: THREE.Object3D) {
        o.traverse((c) => {
            const m = c as THREE.Mesh;
            // Leaf geometry is shared between instances; only free what each part made for itself.
            if (m.geometry && !m.geometry.userData.shared) m.geometry.dispose();
            const mat = m.material as
                THREE.Material | THREE.Material[] | undefined;
            if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
            else mat?.dispose?.();
        });
    }

    private resize() {
        const w = Math.max(1, this.container.clientWidth);
        const h = Math.max(1, this.container.clientHeight);
        this.renderer.setSize(w, h, false);
        this.camera.aspect = w / h;
        // Keep the tomato (and a hat) fully in view on narrow phones by backing the camera off.
        const halfFov = THREE.MathUtils.degToRad(this.camera.fov) / 2;
        const needHalfWidth = 2.7;
        const needHalfHeight = 2.8;
        const distW = needHalfWidth / (this.camera.aspect * Math.tan(halfFov));
        const distH = needHalfHeight / Math.tan(halfFov);
        this.camBase.set(0, 0.2, Math.max(distW, distH, 7));
        this.camera.position.copy(this.camBase);
        this.camera.updateProjectionMatrix();
        this.layoutStage();
        this.backdropDirty = true;
        const px = h * this.renderer.getPixelRatio();
        this.additive.setScale(px, this.camera.fov);
        this.soft.setScale(px, this.camera.fov);
    }

    /** Share of the view (from the bottom) the timer panel covers; the tomato is lifted to sit above it. */
    setReserve(fraction: number) {
        this.reserve = fraction;
        this.layoutStage();
        this.backdropDirty = true;
    }

    private layoutStage() {
        this.stage.position.y = this.halfHeightAtOrigin() * this.reserve;
    }

    private halfWidthAtOrigin() {
        return (
            this.camBase.z *
            Math.tan(THREE.MathUtils.degToRad(this.camera.fov) / 2) *
            this.camera.aspect
        );
    }

    private halfHeightAtOrigin() {
        return (
            this.camBase.z *
            Math.tan(THREE.MathUtils.degToRad(this.camera.fov) / 2)
        );
    }

    // ------------------------------------------------------------------ frame loop

    private frame = () => {
        const now = performance.now();
        const dt = Math.min(0.05, (now - this.lastFrame) / 1000);
        this.lastFrame = now;
        this.time += dt;
        this.update(dt);
        this.renderer.clear();
        if (this.backdrop) {
            this.backdrop.update(this.time, dt);
            // The depth buffer is kept, so backdrop objects in front of the tomato occlude it.
            this.renderer.render(this.backdrop.scene, this.camera);
        }
        this.renderer.render(this.scene, this.camera);
    };

    private update(dt: number) {
        // Tweens
        for (let i = this.tweens.length - 1; i >= 0; i--) {
            const tw = this.tweens[i];
            tw.t += dt;
            const p = Math.min(1, tw.t / tw.d);
            tw.fn(p);
            if (p >= 1) {
                this.tweens.splice(i, 1);
                tw.resolve();
            }
        }

        // Hop: simple gravity, lands on the ground at 0.
        if (this.hop > 0 || this.hopV > 0) {
            this.hopV -= 22 * dt;
            this.hop += this.hopV * dt;
            if (this.hop <= 0) {
                if (this.hopV < -2) this.sqV -= 3;
                this.hop = 0;
                this.hopV = 0;
            }
        }

        // Squash & stretch spring
        const accel = -170 * this.sq - 10 * this.sqV;
        this.sqV += accel * dt;
        this.sq = THREE.MathUtils.clamp(this.sq + this.sqV * dt, -0.45, 0.45);

        const energy = this.running ? 0.35 + 0.65 * this.progress : 0.1;
        const breathe =
            Math.sin(this.time * (2 + energy * 3)) * (0.012 + 0.012 * energy);
        const nervous =
            this.running && this.progress > 0.9
                ? (this.progress - 0.9) * 10
                : 0;

        // Let go of the screen and the tomato slowly looks back at you.
        if (this.pointerReleased) {
            this.pointer.multiplyScalar(Math.max(0, 1 - 1.2 * dt));
        }

        // Idle motion only when no completion animation owns the transform
        if (!this.animating) {
            // The timer model rocks tick-tock once a second while it counts down.
            const rock =
                this.look?.model === 'timer' && this.running
                    ? Math.sin(this.time * Math.PI) * 0.05
                    : 0;
            const wob =
                Math.sin(this.time * (3 + energy * 4)) * 0.03 * energy + rock;
            this.body.rotation.z = THREE.MathUtils.lerp(
                this.body.rotation.z,
                wob,
                8 * dt
            );
            this.body.rotation.y = THREE.MathUtils.lerp(
                this.body.rotation.y,
                this.pointer.x * 0.5,
                4 * dt
            );
            this.body.rotation.x = THREE.MathUtils.lerp(
                this.body.rotation.x,
                -this.pointer.y * 0.28,
                4 * dt
            );
            this.root.position.y =
                Math.sin(this.time * 1.7) * 0.05 +
                Math.max(0, this.beat) * 0.1 +
                this.hop;
            this.root.position.x =
                nervous > 0 ? Math.sin(this.time * 60) * 0.03 * nervous : 0;
        }
        this.beat = Math.max(0, this.beat - dt * 3);

        const s =
            1 +
            breathe +
            this.beat * 0.06 +
            (this.running ? this.progress * 0.03 : 0);
        this.body.scale.set(
            s * (1 + this.sq - this.yawnAmt * 0.06),
            s * (1 - this.sq + this.yawnAmt * 0.1),
            s * (1 + this.sq - this.yawnAmt * 0.06)
        );

        // Ripening: glow and a warm emissive tint build as the session progresses.
        const ripe = this.running ? this.progress : 0;
        this.bodyMat.emissive
            .set(this.look?.color ?? '#e63a2a')
            .multiplyScalar(0.12 + ripe * 0.3);
        this.bodyMat.emissive.lerp(new THREE.Color(0xfff2d0), this.flash);
        (this.glow.material as THREE.MeshBasicMaterial).opacity =
            0.18 +
            ripe * 0.28 +
            this.beat * 0.25 +
            Math.sin(this.time * 2) * 0.03;
        this.glow.scale.setScalar(6.2 + ripe * 1.2 + this.beat * 1.2);

        // Shadow follows the tomato and shrinks with height.
        const h = Math.max(0, this.root.position.y);
        const sh = 1 / (1 + h * 0.35);
        this.shadow.position.x = this.root.position.x;
        // On a backdrop's ground it is a tighter, darker contact shadow.
        const size = sh * (this.backdrop ? 0.8 : 1);
        this.shadow.scale.set(size, size, size);
        (this.shadow.material as THREE.MeshBasicMaterial).opacity = this.root
            .visible
            ? (this.backdrop ? 0.5 : 0.28) * sh
            : 0;

        // Googly pupils: look at the pointer, lag behind the squash.
        if (this.eyes?.pupils) {
            const tx = this.pointer.x * 0.05;
            const ty = this.pointer.y * 0.05 - 0.02 + this.sqV * 0.004;
            this.pupilPos.x = THREE.MathUtils.lerp(
                this.pupilPos.x,
                tx,
                10 * dt
            );
            this.pupilPos.y = THREE.MathUtils.lerp(
                this.pupilPos.y,
                ty,
                10 * dt
            );
            this.eyes.pupils.forEach((p) => {
                p.position.x = THREE.MathUtils.clamp(
                    this.pupilPos.x,
                    -0.07,
                    0.07
                );
                p.position.y = THREE.MathUtils.clamp(
                    this.pupilPos.y,
                    -0.07,
                    0.07
                );
            });
        }
        if (this.eyes?.pulse) {
            const beat =
                0.28 * (1 + 0.14 * Math.sin(this.time * (6 + energy * 6)));
            this.eyes.pulse.forEach((m) =>
                m.scale.setScalar(
                    beat * ((m.userData.baseScale as number) / 0.28 || 1)
                )
            );
        }
        if (this.dial) {
            // The wedge eases towards the time left rather than stepping with the timer's updates.
            const target = Math.min(
                1,
                (this.totalMs * (1 - this.progress)) / (DIAL_MINUTES * 60_000)
            );
            this.dialFrac += (target - this.dialFrac) * Math.min(1, 8 * dt);
            this.dial.setRemaining(this.dialFrac);
        }
        this.vine?.animate(this.time);

        // Detached parts (explosion) fly under gravity.
        this.parts.forEach((part) => {
            if (!part.flying) return;
            part.vel.y -= 9 * dt;
            part.object.position.addScaledVector(part.vel, dt);
            part.object.rotation.x += part.ang.x * dt;
            part.object.rotation.y += part.ang.y * dt;
            part.object.rotation.z += part.ang.z * dt;
        });

        // Ambient motes drifting up behind everything.
        if (!this.reducedMotion && Math.random() < dt * 9) {
            const hw = this.halfWidthAtOrigin();
            const hh = this.halfHeightAtOrigin();
            this.additive.emit({
                pos: new THREE.Vector3(
                    rand(-hw, hw),
                    rand(-hh, -hh * 0.3),
                    rand(-3, -0.5)
                ),
                vel: new THREE.Vector3(rand(-0.1, 0.1), rand(0.3, 0.8), 0),
                color: new THREE.Color(this.look?.color ?? '#ff5544').offsetHSL(
                    rand(-0.05, 0.05),
                    0,
                    0.2
                ),
                size: rand(0.06, 0.22),
                life: rand(2.5, 4.5),
                shrink: true,
            });
        }
        if (
            this.running &&
            !this.animating &&
            Math.random() < dt * (2 + ripe * 14)
        ) {
            // Steam-like sparks off the tomato as it ripens.
            this.additive.emit({
                pos: new THREE.Vector3(
                    rand(-0.6, 0.6),
                    0.95,
                    rand(-0.2, 0.4)
                ).add(this.stage.position),
                vel: new THREE.Vector3(rand(-0.3, 0.3), rand(0.8, 1.8), 0),
                color: new THREE.Color(0xffe2b0),
                size: rand(0.05, 0.12),
                life: rand(0.6, 1.1),
                shrink: true,
            });
        }

        this.additive.update(dt);
        this.soft.update(dt);
        this.chunks.update(dt, this.time);
        this.seeds.update(dt, this.time);
        this.confetti.update(dt, this.time);

        // Camera shake (decaying), applied on top of the resize baseline.
        this.shake = Math.max(0, this.shake - dt * 1.6);
        const k = this.reducedMotion ? 0 : this.shake * this.shake;
        this.camera.position.set(
            this.camBase.x + (Math.random() - 0.5) * k * 0.5,
            this.camBase.y + (Math.random() - 0.5) * k * 0.4,
            this.camBase.z
        );
        this.camera.lookAt(0, 0.1, 0);
        if (this.backdrop) this.aimBackdropCamera(dt);
    }

    // ------------------------------------------------------------------ effects

    private tween(d: number, fn: (p: number) => void): Promise<void> {
        return new Promise((resolve) =>
            this.tweens.push({ t: 0, d: Math.max(0.001, d), fn, resolve })
        );
    }

    private wait(d: number) {
        return this.tween(d, () => undefined);
    }

    private cue(cue: Cue, pitch?: number) {
        this.opts.onCue?.(cue, pitch);
    }

    private shakeBy(amount: number) {
        this.shake = Math.min(1.2, this.shake + amount);
    }

    private worldPos(local: THREE.Vector3): THREE.Vector3 {
        return local.clone().add(this.stage.position);
    }

    private sparkle(amount = 1) {
        const n = Math.round(14 * amount);
        for (let i = 0; i < n; i++) {
            const a = rand(0, Math.PI * 2);
            const speed = rand(1.2, 3.6);
            this.additive.emit({
                pos: this.worldPos(
                    new THREE.Vector3(Math.cos(a) * 0.9, Math.sin(a) * 0.8, 0.6)
                ),
                vel: new THREE.Vector3(
                    Math.cos(a) * speed,
                    Math.sin(a) * speed,
                    rand(0, 1)
                ),
                color: new THREE.Color().setHSL(rand(0, 1), 1, 0.7),
                size: rand(0.1, 0.25),
                life: rand(0.4, 0.8),
                drag: 0.05,
                shrink: true,
            });
        }
    }

    private confettiBurst(from: THREE.Vector3, count: number, power: number) {
        const origin = this.worldPos(from);
        for (let i = 0; i < count; i++) {
            const a = rand(0, Math.PI * 2);
            const up = rand(0.4, 1);
            this.confetti.spawn({
                pos: origin.clone(),
                vel: new THREE.Vector3(
                    Math.cos(a) * power * rand(0.3, 1),
                    up * power * rand(0.6, 1.3),
                    Math.sin(a) * 1.2
                ),
                ang: new THREE.Vector3(
                    rand(-12, 12),
                    rand(-12, 12),
                    rand(-12, 12)
                ),
                scale: rand(0.7, 1.4),
                color: new THREE.Color(CONFETTI[i % CONFETTI.length]),
                life: rand(2.2, 3.6),
                gravity: -5.5,
                drag: 0.35,
                flutter: 1.4,
            });
        }
    }

    private confettiRain(count: number) {
        const hw = this.halfWidthAtOrigin();
        const hh = this.halfHeightAtOrigin();
        for (let i = 0; i < count; i++) {
            this.confetti.spawn({
                pos: new THREE.Vector3(
                    rand(-hw, hw),
                    hh + rand(0.2, 3),
                    rand(-1, 2)
                ),
                vel: new THREE.Vector3(rand(-0.6, 0.6), rand(-3, -1), 0),
                ang: new THREE.Vector3(rand(-8, 8), rand(-8, 8), rand(-8, 8)),
                scale: rand(0.7, 1.3),
                color: new THREE.Color(CONFETTI[i % CONFETTI.length]),
                life: rand(2.5, 4),
                gravity: -1.2,
                drag: 0.5,
                flutter: 1.8,
            });
        }
    }

    private shockwave(
        duration: number,
        size: number,
        at = new THREE.Vector3(0, 0, 0.6)
    ) {
        const mat = this.ring.material as THREE.MeshBasicMaterial;
        this.ring.position.copy(at);
        mat.color
            .set(this.look?.color ?? '#ffffff')
            .lerp(new THREE.Color(0xffffff), 0.6);
        void this.tween(duration, (p) => {
            this.ring.scale.setScalar(0.3 + easeOutCubic(p) * size);
            mat.opacity = (1 - p) * 0.9;
        }).then(() => (mat.opacity = 0));
    }

    /** A shockwave on the ground at the tomato's feet: skipped where there is no ground (space). */
    private groundShockwave(duration: number, size: number, y: number) {
        if (this.backdrop?.hideShadow) return;
        this.shockwave(duration, size, new THREE.Vector3(0, y, 0.6));
    }

    private bigFlash(duration: number, size: number) {
        const mat = this.flashSprite.material as THREE.MeshBasicMaterial;
        void this.tween(duration, (p) => {
            this.flashSprite.scale.setScalar(2 + easeOutCubic(p) * size);
            mat.opacity = (1 - p) * (1 - p) * 0.95;
        }).then(() => (mat.opacity = 0));
    }

    private dust(x: number, y: number, amount = 1) {
        const n = Math.max(1, Math.round(amount));
        for (let i = 0; i < n; i++) {
            this.soft.emit({
                pos: new THREE.Vector3(
                    x + rand(-0.3, 0.3),
                    y,
                    rand(-0.2, 0.5)
                ).add(this.stage.position),
                vel: new THREE.Vector3(rand(-1.2, 1.2), rand(0.4, 1.6), 0),
                color: new THREE.Color(0xe9dfd0),
                size: rand(0.25, 0.6),
                life: rand(0.5, 0.9),
                drag: 0.1,
                shrink: true,
            });
        }
    }

    /** Puts the tomato and all its parts back exactly as they were, ready for idling. */
    private settle() {
        this.parts.forEach((part) => this.reattach(part));
        this.root.visible = true;
        this.root.position.set(0, 0, 0);
        this.spin.position.set(0, 0, 0);
        this.spin.rotation.set(0, 0, 0);
        this.spin.scale.set(1, 1, 1);
        this.flash = 0;
        this.animating = false;
    }

    private reattach(part: Part) {
        if (!part.flying) return;
        part.flying = false;
        const parent =
            part.object === this.hat?.group ? this.hatMount : this.body;
        parent.add(part.object);
        part.object.position.copy(part.homePos);
        part.object.quaternion.copy(part.homeQuat);
        part.object.scale.set(1, 1, 1);
    }

    private flingParts(power: number) {
        this.parts.forEach((part) => {
            this.scene.attach(part.object);
            part.flying = true;
            const dir = new THREE.Vector3(
                rand(-1, 1),
                rand(0.1, 1),
                rand(-0.2, 1)
            ).normalize();
            part.vel.copy(dir.multiplyScalar(power * rand(0.7, 1.2)));
            part.ang.set(rand(-9, 9), rand(-9, 9), rand(-9, 9));
        });
    }

    // ------------------------------------------------------------------ completion animations

    private async respawn(fanfare = true) {
        this.parts.forEach((p) => this.reattach(p));
        this.root.visible = true;
        this.root.position.set(0, 0, 0);
        this.spin.position.set(0, 0, 0);
        this.spin.rotation.set(0, 0, 0);
        this.spin.scale.setScalar(0.001);
        this.flash = 0;
        this.cue('respawn');
        this.cue('hihi');
        if (fanfare) this.cue('fanfare');
        this.confettiRain(130);
        this.confettiBurst(new THREE.Vector3(0, 0.5, 0.5), 90, 8);
        this.sparkle(2);
        await this.tween(0.8, (p) =>
            this.spin.scale.setScalar(Math.max(0.001, easeOutElastic(p)))
        );
        this.poke();
    }

    private async explode() {
        // Wind-up: swell, shake, glow.
        this.cue('anticipate');
        await this.tween(0.85, (p) => {
            const e = easeInCubic(p);
            this.spin.scale.setScalar(1 + 0.45 * e);
            this.spin.rotation.z = Math.sin(p * 70) * 0.06 * (0.3 + e);
            this.spin.position.x = Math.sin(p * 95) * 0.05 * e;
            this.flash = e * 0.85;
            this.shake = Math.max(this.shake, 0.15 * e);
        });

        // Boom.
        this.cue('explode');
        this.cue('wheee');
        this.root.visible = false;
        this.flash = 0;
        this.spin.scale.setScalar(1);
        this.spin.rotation.set(0, 0, 0);
        this.spin.position.set(0, 0, 0);
        this.shakeBy(1.2);
        this.bigFlash(0.5, 16);
        this.shockwave(0.7, 9);
        this.flingParts(8);

        const base = new THREE.Color(this.look?.color ?? '#e63a2a');
        const origin = this.stage.position;
        for (let i = 0; i < 110; i++) {
            const dir = new THREE.Vector3(
                rand(-1, 1),
                rand(-0.6, 1),
                rand(-0.3, 1)
            ).normalize();
            const col = base.clone();
            const roll = Math.random();
            if (roll < 0.25)
                col.lerp(new THREE.Color(0xffd0c0), 0.5); // paler flesh
            else col.offsetHSL(0, 0, rand(-0.12, 0.08));
            this.chunks.spawn({
                pos: dir
                    .clone()
                    .multiplyScalar(0.55)
                    .setY(dir.y * 0.5)
                    .add(origin),
                vel: dir
                    .clone()
                    .multiplyScalar(rand(3, 9.5))
                    .add(new THREE.Vector3(0, rand(1, 4), 0)),
                ang: new THREE.Vector3(
                    rand(-14, 14),
                    rand(-14, 14),
                    rand(-14, 14)
                ),
                scale: rand(0.5, 1.5),
                color: col,
                life: rand(2.6, 3.4),
                gravity: -11,
                drag: 0.8,
            });
        }
        for (let i = 0; i < 44; i++) {
            const dir = new THREE.Vector3(
                rand(-1, 1),
                rand(-0.3, 1),
                rand(-0.3, 1)
            ).normalize();
            this.seeds.spawn({
                pos: dir.clone().multiplyScalar(0.4).add(origin),
                vel: dir
                    .clone()
                    .multiplyScalar(rand(2, 7))
                    .add(new THREE.Vector3(0, rand(1, 3), 0)),
                ang: new THREE.Vector3(
                    rand(-10, 10),
                    rand(-10, 10),
                    rand(-10, 10)
                ),
                scale: rand(0.7, 1.3),
                color: new THREE.Color(0xf4e9a8),
                life: rand(2.4, 3.2),
                gravity: -9,
                drag: 0.85,
            });
        }
        for (let i = 0; i < 260; i++) {
            const dir = new THREE.Vector3(
                rand(-1, 1),
                rand(-1, 1),
                rand(-0.4, 1)
            ).normalize();
            this.soft.emit({
                pos: dir.clone().multiplyScalar(0.5).add(origin),
                vel: dir.multiplyScalar(rand(1.5, 11)),
                color: base.clone().offsetHSL(0, 0, rand(-0.1, 0.1)),
                size: rand(0.12, 0.42),
                life: rand(0.7, 1.7),
                gravity: -7,
                drag: 0.35,
                shrink: true,
            });
        }
        for (let i = 0; i < 120; i++) {
            const dir = new THREE.Vector3(
                rand(-1, 1),
                rand(-1, 1),
                rand(-0.2, 1)
            ).normalize();
            this.additive.emit({
                pos: origin.clone(),
                vel: dir.multiplyScalar(rand(4, 14)),
                color: new THREE.Color().setHSL(rand(0.02, 0.15), 1, 0.65),
                size: rand(0.06, 0.2),
                life: rand(0.3, 0.9),
                drag: 0.2,
                shrink: true,
            });
        }

        await this.wait(1.9);
        await this.respawn();
    }

    private async rollAway() {
        this.cue('wheee');
        const exitX = this.halfWidthAtOrigin() + 1.8;

        // Crouch.
        await this.tween(0.32, (p) => {
            const e = Math.sin((p * Math.PI) / 2);
            this.spin.scale.set(1 + 0.22 * e, 1 - 0.3 * e, 1 + 0.22 * e);
            this.spin.position.y = -0.14 * e;
        });
        this.spin.position.y = 0;
        this.spin.scale.set(1, 1, 1);
        this.sqV = -3;

        // Roll off to the right, hopping, kicking up dust.
        this.cue('roll');
        let lastHop = 0;
        const hops = 6;
        await this.tween(1.8, (p) => {
            const x = easeInQuad(p) * exitX * 1.05;
            this.root.position.x = x;
            this.spin.rotation.z = -x;
            const phase = p * hops;
            const hop =
                Math.abs(Math.sin(phase * Math.PI)) * 0.55 * (1 - p * 0.35);
            this.root.position.y = hop;
            const idx = Math.floor(phase);
            if (idx !== lastHop) {
                lastHop = idx;
                this.cue('bounce', 0.8 + p * 0.8);
                this.dust(x, -0.95, 5);
                this.sqV += 2.5;
            }
            this.dustTimer += 1;
            if (this.dustTimer % 2 === 0 && hop < 0.15)
                this.dust(x - 0.4, -0.95, 1);
        });
        this.root.visible = false;
        this.root.position.set(0, 0, 0);
        this.spin.rotation.set(0, 0, 0);
        await this.wait(0.55);

        // A fresh one rolls in from the left, slowing to a stop.
        this.root.visible = true;
        this.cue('fanfare');
        this.cue('roll');
        this.confettiBurst(
            new THREE.Vector3(-this.halfWidthAtOrigin() * 0.6, -1, 0.5),
            80,
            9
        );
        this.confettiBurst(
            new THREE.Vector3(this.halfWidthAtOrigin() * 0.6, -1, 0.5),
            80,
            9
        );
        await this.tween(1.35, (p) => {
            const e = easeOutCubic(p);
            const x = -exitX * (1 - e);
            this.root.position.x = x;
            this.spin.rotation.z = -x;
            if (p < 0.8 && Math.random() < 0.5) this.dust(x - 0.4, -0.95, 1);
        });
        this.root.position.x = 0;
        this.spin.rotation.z = 0;
        this.cue('land');
        this.cue('hihi');
        this.sqV = -6;
        this.shakeBy(0.35);
        this.dust(0, -0.95, 10);
        this.sparkle(2);
        await this.wait(0.4);
    }

    private async rocket() {
        this.cue('wheee');
        const topY = this.halfHeightAtOrigin() + 2.6;
        const flame = (power: number) => {
            const x = this.root.position.x;
            const y = this.root.position.y - 0.85;
            for (let i = 0; i < 4; i++) {
                this.additive.emit({
                    pos: new THREE.Vector3(
                        x + rand(-0.25, 0.25),
                        y,
                        rand(-0.2, 0.3)
                    ).add(this.stage.position),
                    vel: new THREE.Vector3(
                        rand(-0.6, 0.6),
                        -rand(2, 5) * power,
                        0
                    ),
                    color: new THREE.Color().setHSL(
                        rand(0.04, 0.14),
                        1,
                        rand(0.55, 0.75)
                    ),
                    size: rand(0.25, 0.55) * (0.6 + power * 0.5),
                    life: rand(0.25, 0.55),
                    shrink: true,
                });
            }
            this.soft.emit({
                pos: new THREE.Vector3(
                    x + rand(-0.3, 0.3),
                    y - 0.2,
                    rand(-0.2, 0.3)
                ).add(this.stage.position),
                vel: new THREE.Vector3(rand(-1, 1), -rand(0.5, 2), 0),
                color: new THREE.Color(0xf3ece4),
                size: rand(0.4, 0.9),
                life: rand(0.7, 1.3),
                drag: 0.2,
                shrink: true,
            });
        };

        // Ignition: rumble and flame.
        this.cue('launch');
        await this.tween(0.95, (p) => {
            this.spin.position.x = Math.sin(p * 120) * 0.04 * (0.3 + p);
            this.spin.scale.set(1 - 0.05 * p, 1 + 0.12 * p, 1 - 0.05 * p);
            this.flash = p * 0.25;
            this.shake = Math.max(this.shake, 0.12 + 0.35 * p);
            flame(0.4 + p * 0.8);
        });

        // Lift-off.
        this.flash = 0;
        this.groundShockwave(0.6, 7, -1);
        this.dust(0, -0.95, 14);
        await this.tween(1.35, (p) => {
            const e = easeInCubic(p);
            this.root.position.y = e * topY;
            this.spin.position.x = 0;
            this.spin.scale.set(1 - 0.14 * e, 1 + 0.4 * e, 1 - 0.14 * e);
            this.spin.rotation.y = p * p * 18;
            this.shake = Math.max(this.shake, 0.5 * (1 - p));
            flame(1.2 + p * 1.5);
        });
        this.root.visible = false;
        this.bigFlash(0.45, 12);
        this.confettiBurst(new THREE.Vector3(0, topY * 0.85, 0.5), 140, 9);
        this.sparkle(2.5);
        await this.wait(0.7);

        // Splash back down.
        this.root.visible = true;
        this.spin.rotation.set(0, 0, 0);
        this.spin.scale.set(1, 1, 1);
        this.root.position.y = topY;
        this.cue('fanfare');
        this.confettiRain(150);
        await this.tween(0.6, (p) => {
            this.root.position.y = topY * (1 - easeInQuad(p));
            this.spin.rotation.z = (1 - p) * 4;
            this.spin.scale.set(1 - 0.08 * p, 1 + 0.2 * p, 1 - 0.08 * p);
        });
        this.root.position.y = 0;
        this.spin.rotation.z = 0;
        this.spin.scale.set(1, 1, 1);
        this.cue('land');
        this.cue('hihi');
        this.sqV = -7;
        this.shakeBy(0.6);
        this.groundShockwave(0.7, 7, -0.9);
        this.dust(0, -0.95, 18);
        await this.tween(0.55, (p) => {
            this.root.position.y =
                Math.abs(Math.sin(p * Math.PI * 2)) * 0.5 * (1 - p);
            if (p > 0.45 && p < 0.5) this.cue('bounce', 1.2);
        });
        this.root.position.y = 0;
        this.sparkle(2);
        await this.wait(0.3);
    }
}

export type { FeatureBuild };
